import { useState, useEffect } from 'react';
import { planService, Plan } from '../../../services/plan.service';
import { tenantPlanService } from '../../../services/tenantPlan.service';
import { toast } from 'react-hot-toast';

interface Tenant {
  _id: string;
  name: string;
  subdomain: string;
  status: string;
}

interface TenantPlanAssignmentProps {
  tenants: Tenant[];
  onAssignmentComplete?: () => void;
}

export default function TenantPlanAssignment({ tenants, onAssignmentComplete }: TenantPlanAssignmentProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    tenantId: '',
    planId: '',
    startDate: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await planService.getAllPlans(true); // Get active plans only
      setPlans(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.tenantId || !formData.planId) {
      toast.error('Please select both firm and plan');
      return;
    }

    try {
      setSubmitting(true);
      await tenantPlanService.assignPlanToTenant({
        tenantId: formData.tenantId,
        planId: formData.planId,
        startDate: formData.startDate,
        notes: formData.notes,
      });
      
      toast.success('Plan assigned successfully');
      
      // Reset form
      setFormData({
        tenantId: '',
        planId: '',
        startDate: new Date().toISOString().split('T')[0],
        notes: '',
      });
      
      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to assign plan');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPlan = plans.find((p) => p._id === formData.planId);
  const selectedTenant = tenants.find((t) => t._id === formData.tenantId);

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6">Assign Plan to Firm</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Firm Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Firm *
          </label>
          <select
            required
            value={formData.tenantId}
            onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">-- Select Firm --</option>
            {tenants.map((tenant) => (
              <option key={tenant._id} value={tenant._id}>
                {tenant.name} ({tenant.subdomain})
              </option>
            ))}
          </select>
          {selectedTenant && (
            <p className="mt-1 text-sm text-gray-500">
              Status: <span className="font-medium">{selectedTenant.status}</span>
            </p>
          )}
        </div>

        {/* Plan Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Plan *
          </label>
          {loading ? (
            <div className="text-sm text-gray-500">Loading plans...</div>
          ) : (
            <>
              <select
                required
                value={formData.planId}
                onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">-- Select Plan --</option>
                {plans.map((plan) => (
                  <option key={plan._id} value={plan._id}>
                    {plan.displayName} - {plan.currency} ${plan.cost}/{plan.duration}
                  </option>
                ))}
              </select>
              
              {selectedPlan && (
                <div className="mt-3 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm font-medium text-blue-900">{selectedPlan.displayName}</p>
                  <p className="text-sm text-blue-700 mt-1">{selectedPlan.description}</p>
                  <div className="mt-2 flex items-center space-x-4 text-sm text-blue-700">
                    <span>Users: {selectedPlan.maxUsers}</span>
                    <span>Clients: {selectedPlan.maxClients}</span>
                    <span>Duration: {selectedPlan.durationInDays} days</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date *
          </label>
          <input
            type="date"
            required
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          {selectedPlan && formData.startDate && (
            <p className="mt-1 text-sm text-gray-500">
              End Date: {new Date(new Date(formData.startDate).getTime() + selectedPlan.durationInDays * 24 * 60 * 60 * 1000).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Add any notes about this assignment..."
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Assigning...' : 'Assign Plan'}
          </button>
        </div>
      </form>
    </div>
  );
}
